import { useEffect, useRef } from 'react';

import * as braze from '@braze/web-sdk';
import styled from '@emotion/styled';
import {
    BRAZE_EVENT, colors, formatLonghandDateTimeWithWeekday, heapTrack
} from '@modernanimal/common';
// eslint-disable-next-line import/no-extraneous-dependencies
import posthog from 'posthog-js';
import { useSearchParams } from 'react-router-dom';

import { CreateAppointmentMutation } from './__generated__/graphql';
import { bingTrackingPurchaseEvent } from './BingTracking';
import PrimaryButton from './components/PrimaryButton';
import Fonts from './fonts';
import {
    getClinicDirectionsUrl, DOWNLOAD_APP_LINK, HREF_TARGET,
} from './helpers';
import Icons from './icons';
import useWebBookingStore, { LOCAL_STORAGE_KEY, PatientStoreType } from './store';

type Props = {
    locationShowsCreditCardStep: boolean
}

// Helper function to generate ICS file content
const generateICSContent = (createdAppointment: CreateAppointmentMutation['createAppointment'], patient: PatientStoreType) => {
    const {
        location, start, staff, end
    } = createdAppointment;

    // Parse start and end times
    // The start and end are already in ISO format with timezone offset
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date(startDate.getTime() + 30 * 60000);

    // Format dates for ICS file - format: YYYYMMDDTHHMMSSZ
    // The Z indicates UTC time, which is what we want for ICS files
    const formatICSDate = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

    // Get the dates in proper UTC format for ICS
    const icsStart = formatICSDate(startDate);
    const icsEnd = formatICSDate(endDate);
    const now = formatICSDate(new Date());

    // Format the location for better calendar app compatibility
    const formattedLocationAddress = `${location.address}, ${location.city}, ${location.state} ${location.zipcode}`;

    // In the ICS format, commas and other special characters need extra attention
    // We need to escape the address properly for the LOCATION field
    const escapedLocationForICS = formattedLocationAddress.replace(/,/g, '\\,');

    // Create the ICS content with improved location formatting
    const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Modern Animal//Appointment//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `DTSTART:${icsStart}`,
        `DTEND:${icsEnd}`,
        `DTSTAMP:${now}`,
        `UID:${new Date().getTime()}@modernanimal.com`,
        `SUMMARY:Appointment with ${staff?.name || 'Modern Animal'}`,
        `DESCRIPTION:Appointment for ${patient.name} at ${location.name}\\n${formattedLocationAddress}`,
        `LOCATION:${escapedLocationForICS}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'END:VEVENT',
        'END:VCALENDAR'
    ].join('\r\n');

    return icsContent;
};

// Function to handle the click and trigger calendar download
const handleAddToCalendar = (createdAppointment: CreateAppointmentMutation['createAppointment'], patient: PatientStoreType) => {
    // Generate ICS content
    const icsContent = generateICSContent(createdAppointment, patient);

    // Create a blob and download link
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    // Create and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = 'appointment.ics'; // Match Python filename
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const AppointmentConfirmation = ({ locationShowsCreditCardStep }: Props) => {
    const { appointment, patient } = useWebBookingStore((state) => ({
        appointment: state.appointment,
        patient: state.patient
    }));
    const [params] = useSearchParams();
    // Add this ref to track if Braze events have been logged
    const brazeEventsLogged = useRef(false);

    useEffect(() => {
        const clearLocalStorage = () => { localStorage.removeItem(LOCAL_STORAGE_KEY); };
        window.addEventListener('beforeunload', clearLocalStorage);

        return () => window.removeEventListener('beforeunload', clearLocalStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track heap event if this is a VC member making an appointment
    // on behalf of a new member
    useEffect(() => {
        const fromVC = params.get('fromVC');

        if (fromVC === 'true') {
            heapTrack('Web Booking - Submit - Appointment Confirmation (via VC)');
            posthog.capture('web_booking_view', { screen: 'AppointmentConfirmationViaVC' });
        }
    }, [params]);

    // Add Bing tracking event
    useEffect(() => {
        bingTrackingPurchaseEvent();
    }, []);

    useEffect(() => {
        // Only run this effect once
        if (brazeEventsLogged.current) return;
        braze.logCustomEvent(BRAZE_EVENT.ONBOARDING_COMPLETED);

        // Mark that we've logged these events
        brazeEventsLogged.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!appointment.createdAppointment) {
        return null;
    }

    const {
        location, dynamicText, start, staff, reasonForVisitDescription
    } = appointment.createdAppointment;

    const renderHeader = () => (
        <Section removePaddingTop data-testid="allSetHeader">
            <Fonts.Heading04>{'You\'re all set!'}</Fonts.Heading04>
            <ClinicImage src={location.image?.url ?? ''} />
            <Subsection>
                <Fonts.Heading06>
                    {formatLonghandDateTimeWithWeekday(start, location.region.timezone)}
                </Fonts.Heading06>
                <CalendarLink
                    onClick={() => appointment.createdAppointment
                        && handleAddToCalendar(appointment.createdAppointment, patient)}
                >
                    Add to calendar
                </CalendarLink>
            </Subsection>
        </Section>
    );

    const renderAppointmentDetails = () => (
        <Section data-testid="appointmentDetailsSection">
            <Details>
                <Fonts.LabelMedium style={{ color: colors.GREY_1 }}>{location.name}</Fonts.LabelMedium>
                <Fonts.Body style={{ color: colors.GREY_2 }}>
                    {location.address}
                    <br />
                    {`${location.city}, ${location.state} ${location.zipcode}`}
                </Fonts.Body>
            </Details>
            <Fonts.LabelMedium>
                <Link href={getClinicDirectionsUrl(location)} target="_blank">
                    Get directions
                </Link>
            </Fonts.LabelMedium>
            <AppointmentDetailsRow>
                <DescriptorText>Provider:</DescriptorText>
                <Fonts.Body>{staff?.name ?? ''}</Fonts.Body>
            </AppointmentDetailsRow>
            <AppointmentDetailsRow>
                <DescriptorText>Reason for visit:</DescriptorText>
                <Fonts.Body>{reasonForVisitDescription}</Fonts.Body>
            </AppointmentDetailsRow>
        </Section>
    );

    const renderAppInfo = () => (
        <Section data-testid="appInfoSection">
            <Row>
                <IconContainer>
                    <Icons.Download height={24} width={24} fill={colors.GREY_1} />
                </IconContainer>
                <Subsection>
                    <Details>
                        <Fonts.LabelMedium>Download our app</Fonts.LabelMedium>
                        <Fonts.Body style={{ color: colors.GREY_2 }}>
                            Connect with our team of licensed experts, day or night, through the Modern Animal app.
                        </Fonts.Body>
                    </Details>
                    <PrimaryButton color="white" onClick={() => window.open(DOWNLOAD_APP_LINK, HREF_TARGET)}>
                        Download app
                    </PrimaryButton>
                </Subsection>
            </Row>
        </Section>
    );

    const renderChangeOfPlans = () => (
        <Section data-testid="changeOfPlansSection">
            <Row>
                <IconContainer>
                    <Icons.Exit height={24} width={24} fill={colors.GREY_1} />
                </IconContainer>
                <Details>
                    <Fonts.LabelMedium>Change of plans?</Fonts.LabelMedium>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {'Call '}
                        <Link
                            href={`tel:${location.phone}`}
                            style={{ textDecoration: 'none' }}
                        >
                            {location.phone}
                        </Link>
                        {' to cancel or reschedule this appointment. '}
                        {dynamicText?.changeOfPlansText}
                    </Fonts.Body>
                </Details>
            </Row>
        </Section>
    );

    const renderDayOfVisitInstructions = () => (
        <Section data-testid="dayOfVisitSection">
            <Row>
                <IconContainer>
                    <Icons.Clock height={24} width={24} fill={colors.GREY_1} />
                </IconContainer>
                <Details>
                    <Fonts.LabelMedium>On the day of your visit</Fonts.LabelMedium>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {location.visitorInformation}
                        <br />
                    </Fonts.Body>
                    <InstructionsList>
                        {dynamicText?.dayOfVisitInstructions.map((instruction) => (
                            <li key={instruction}>{instruction}</li>
                        ))}
                    </InstructionsList>
                </Details>
            </Row>
        </Section>
    );

    const renderCashlessClinic = () => (
        <Section data-testid="cashlessClinicSection">
            <Row>
                <IconContainer>
                    <Icons.DollarCircle height={24} width={24} fill={colors.PERIWINKLE_1} />
                </IconContainer>
                <Details>
                    <Fonts.LabelMedium>Modern Animal is a cashless clinic.</Fonts.LabelMedium>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        For a seamless visit, be sure to bring a credit card with you.
                    </Fonts.Body>

                </Details>
            </Row>
        </Section>
    );

    return (
        <AppointmentConfirmationContainer>
            {renderHeader()}
            {renderAppointmentDetails()}
            {renderAppInfo()}
            {renderChangeOfPlans()}
            {renderDayOfVisitInstructions()}
            {renderCashlessClinic()}
        </AppointmentConfirmationContainer>
    );
};

export default AppointmentConfirmation;

const AppointmentConfirmationContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 24px;
`;

const ClinicImage = styled.img`
    width: 100%;
    height: 169px;
    border-radius: 10px;
    object-fit: cover;
`;

const DescriptorText = styled(Fonts.LabelMedium)`
    white-space: nowrap;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const IconContainer = styled.div`
    height: 24px;
    width: 24px;
`;

const InstructionsList = styled.ol`
    padding-inline-start: 16px;
    color: ${colors.GREY_2};
`;

const Link = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: flex-start;
`;

const AppointmentDetailsRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
`;

const Section = styled.div<{ removePaddingTop?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 16px 0px;
    ${({ removePaddingTop }) => removePaddingTop && 'padding-top: 0px;'}
    gap: 16px;
    border-bottom: 1px solid ${colors.PERIWINKLE_4};
`;

const Subsection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CalendarLink = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
    text-decoration: underline;
`;
